import { makeAutoObservable } from 'mobx'
import type { IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowSendSMSStep } from 'entities/SmartCall/api/types'
import { FlowActionStore } from 'widgets/Flow'
import { SendSMSBlockStore } from 'widgets/Flow/blocks/SendSMS'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import {
  allSmartCallFlowActionTypes,
  type ISmartCallFlowActionType,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

export type ISendSMSStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowSendSMSStep['args']
}

export class SendSMSStore {
  static readonly type = 'sendMessage' satisfies ISmartCallFlowActionType
  static title = 'Send SMS'
  static iconProps: IIconProps = {
    icon: 'messageTextCircle02',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore

  readonly sendSMSBlockStore: SendSMSBlockStore

  constructor(private _entities: IEntitiesStore, { id, args, ...props }: ISendSMSStoreProps) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: SendSMSStore.type,
      name: SendSMSStore.title,
      allowedBeforeActions: allSmartCallFlowActionTypes.filter((type) => type !== 'sendMessage'),
      allowedNextActions: [],
    })
    this.sendSMSBlockStore = new SendSMSBlockStore(
      this._entities,
      args
        ? {
            sms: args,
            sendFrom: args,
          }
        : undefined,
      { autoSelectInbox: !args }
    )
    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowSendSMSStep, 'id' | 'type'> = {
    id: SendSMSStore.type,
    type: 'action',
  }

  get step(): ISmartCallFlowSendSMSStep {
    return {
      ...this._stepIdType,
      args: {
        ...this.sendSMSBlockStore.params.sendFrom,
        ...this.sendSMSBlockStore.params.sms,
      },
      next: this.actionStore.nextId,
    }
  }

  setApiError = (key: string, message: string) => {
    if (key.includes('body') || key.includes('mediaUrls')) {
      this.sendSMSBlockStore.setSmsError(message)
    }
    if (key.includes('inboxId') || key.includes('sendingOptions')) {
      this.sendSMSBlockStore.setSendFromError(message)
    }
  }

  validate = () => {
    return this.sendSMSBlockStore.validate()
  }

  get isConfigurationError() {
    return this.sendSMSBlockStore.isConfigurationError
  }
}
